// Inspections et contrôles (M23, contrôles de M09, lot L3-B, D-045, D-046) : plans de contrôle,
// formulaires de constat configurables en JSON versionné (points de contrôle conforme, non conforme,
// sans objet ; observation ; photo exigée), ordres de mission numérotés OM-AAAA-NNNN ciblant un marché,
// un emplacement, une autorisation du domaine public, une intervention ou un lieu libre, constats saisis
// au bureau ou reçus par la synchronisation terrain (opération « constat », idempotente, conflit sur la
// version de l'ordre), mesures correctives à échéance en jours ouvrés, transmission à l'autorité (PDF et
// notification). Aucune sanction n'est prononcée.
import { z } from 'zod';
import { schemaBbox } from './dossiers';
import { schemaPosition } from './marches-structure';
import { schemaCodeProprete, schemaEnveloppeOperation, schemaJourCivil } from './proprete';

const uuid = z.string().uuid('Identifiant invalide');

export const OBJETS_ORDRE_MISSION = {
  marche: 'Marché',
  emplacement: 'Emplacement de marché',
  autorisation_domaine_public: 'Autorisation du domaine public',
  intervention: 'Intervention',
  lieu: 'Lieu libre',
} as const;
export type ObjetOrdreMission = keyof typeof OBJETS_ORDRE_MISSION;
export const CODES_OBJETS_ORDRE = Object.keys(OBJETS_ORDRE_MISSION) as [ObjetOrdreMission, ...ObjetOrdreMission[]];

export const ETATS_ORDRE_MISSION = { emis: 'Émis', en_cours: 'En cours', constat_recu: 'Constat reçu', conflit: 'Conflit', clos: 'Clos', annule: 'Annulé' } as const;
export type EtatOrdreMission = keyof typeof ETATS_ORDRE_MISSION;
export const CODES_ETATS_ORDRE = Object.keys(ETATS_ORDRE_MISSION) as [EtatOrdreMission, ...EtatOrdreMission[]];

/** Transitions manuelles d'un ordre de mission (état de départ → états d'arrivée) ; constat_recu et conflit sont posés par le serveur. */
export const TRANSITIONS_ORDRE_MISSION: Record<EtatOrdreMission, EtatOrdreMission[]> = {
  emis: ['en_cours', 'annule'],
  en_cours: ['annule'],
  constat_recu: ['clos'],
  conflit: ['constat_recu', 'clos'],
  clos: [],
  annule: [],
};

export const ETATS_CONSTAT = { synchronise: 'Synchronisé', conflit: 'En conflit', retenu: 'Retenu', ecarte: 'Écarté', transmis: 'Transmis' } as const;
export type EtatConstat = keyof typeof ETATS_CONSTAT;
export const CODES_ETATS_CONSTAT = Object.keys(ETATS_CONSTAT) as [EtatConstat, ...EtatConstat[]];

export const ETATS_MESURE = { demandee: 'Demandée', realisee: 'Réalisée', non_realisee: 'Non réalisée', transmise: 'Transmise à l\'autorité' } as const;
export type EtatMesure = keyof typeof ETATS_MESURE;
export const CODES_ETATS_MESURE = Object.keys(ETATS_MESURE) as [EtatMesure, ...EtatMesure[]];

export const REPONSES_POINT = { conforme: 'Conforme', non_conforme: 'Non conforme', sans_objet: 'Sans objet' } as const;
export type ReponsePoint = keyof typeof REPONSES_POINT;
export const CODES_REPONSES = Object.keys(REPONSES_POINT) as [ReponsePoint, ...ReponsePoint[]];

export const CONCLUSIONS_CONSTAT = { conforme: 'Conforme', non_conforme: 'Non conforme', sans_objet: 'Sans objet' } as const;
export type ConclusionConstat = keyof typeof CONCLUSIONS_CONSTAT;

export const DECISIONS_CONFLIT = ['retenir', 'ecarter'] as const;

// Formulaires de constat (JSON versionné) -----------------------------------------------------

export const schemaPointFormulaire = z.strictObject({
  code: z.string().trim().regex(/^[a-z][a-z0-9_]{1,40}$/, 'Code en minuscules, chiffres et soulignés'),
  libelle: z.string().trim().min(2).max(200),
  aide: z.string().trim().max(500).optional(),
  photo_exigee: z.boolean().default(false),
  /** Observation : jamais, facultative, ou obligatoire ; une non-conformité en exige toujours une. */
  observation: z.enum(['aucune', 'facultative', 'obligatoire']).default('facultative'),
});
export type PointFormulaire = z.infer<typeof schemaPointFormulaire>;

export const schemaPointsFormulaire = z
  .array(schemaPointFormulaire)
  .min(1, 'Un formulaire compte au moins un point de contrôle')
  .max(100)
  .refine((p) => new Set(p.map((x) => x.code)).size === p.length, 'Deux points portent le même code');

export const schemaCreationFormulaire = z.strictObject({
  code: schemaCodeProprete.optional(),
  nom: z.string().trim().min(2).max(200),
  description: z.string().trim().max(1000).optional(),
  /** Objets que ce formulaire sait contrôler (tous si vide). */
  objets: z.array(z.enum(CODES_OBJETS_ORDRE)).max(5).default([]),
  points: schemaPointsFormulaire,
});

/** Nouvelle version d'un formulaire : les ordres déjà émis gardent la version qu'ils citent. */
export const schemaNouvelleVersionFormulaire = z.strictObject({
  nom: z.string().trim().min(2).max(200).optional(),
  description: z.string().trim().max(1000).optional(),
  objets: z.array(z.enum(CODES_OBJETS_ORDRE)).max(5).optional(),
  points: schemaPointsFormulaire,
});

// Plans et ordres de mission -----------------------------------------------------------------

export const schemaCibleControle = z.strictObject({
  objet_type: z.enum(CODES_OBJETS_ORDRE),
  objet_id: uuid.optional(),
  libelle: z.string().trim().max(200).optional(),
});

export const schemaCreationPlanControle = z
  .strictObject({
    code: schemaCodeProprete.optional(),
    nom: z.string().trim().min(2).max(200),
    periode_debut: schemaJourCivil,
    periode_fin: schemaJourCivil,
    objets_cibles: z.array(schemaCibleControle).max(200).default([]),
    responsable_appartenance_id: uuid.optional(),
  })
  .refine((v) => v.periode_fin >= v.periode_debut, { message: 'La fin de la période précède son début', path: ['periode_fin'] });

export const schemaCreationOrdreMission = z
  .strictObject({
    plan_id: uuid.optional(),
    controleur_appartenance_id: uuid,
    objet_type: z.enum(CODES_OBJETS_ORDRE),
    objet_id: uuid.optional(),
    lieu: z.string().trim().max(300).optional(),
    position: schemaPosition.optional(),
    periode_debut: schemaJourCivil,
    periode_fin: schemaJourCivil,
    formulaire_id: uuid,
    consignes: z.string().trim().max(2000).optional(),
  })
  .refine((v) => v.objet_type === 'lieu' ? Boolean(v.lieu || v.position) : Boolean(v.objet_id), {
    message: 'Un ordre cible un objet (identifiant) ou, pour un lieu libre, une adresse ou une position',
    path: ['objet_id'],
  })
  .refine((v) => v.periode_fin >= v.periode_debut, { message: 'La fin de la période précède son début', path: ['periode_fin'] });

export const schemaTransitionOrdre = z.strictObject({ motif: z.string().trim().min(3).max(500).optional() });

export const schemaFiltreOrdres = z.strictObject({
  etat: z.enum(CODES_ETATS_ORDRE).optional(),
  objet_type: z.enum(CODES_OBJETS_ORDRE).optional(),
  objet_id: uuid.optional(),
  plan_id: uuid.optional(),
  controleur_appartenance_id: uuid.optional(),
  /** Ordres dont la période contient le jour donné. */
  jour: schemaJourCivil.optional(),
  limite: z.coerce.number().int().min(1).max(500).default(100),
});

// Constats -----------------------------------------------------------------------------------

export const schemaReponseConstat = z.strictObject({
  code: z.string().trim().min(2).max(41),
  reponse: z.enum(CODES_REPONSES),
  observation: z.string().trim().max(1000).optional(),
  /** Photo déjà rattachée (document) ou identifiant client d'une photo envoyée à la synchronisation. */
  photo_document_id: uuid.optional(),
  photo_id_client: uuid.optional(),
});
export type ReponseConstat = z.infer<typeof schemaReponseConstat>;

/** Mesure corrective proposée par le contrôleur sur le terrain (D-122, D-133) : créée à la réception d'un constat non conforme appliqué. */
export const schemaMesureProposee = z.strictObject({
  description: z.string().trim().min(3).max(1000),
  delai_jours: z.number().int().min(1).max(365),
});

export const schemaConstat = z.strictObject({
  reponses: z.array(schemaReponseConstat).min(1).max(100),
  observations: z.string().trim().max(4000).optional(),
  position: schemaPosition.optional(),
  effectue_le: z.iso.datetime({ offset: true }).optional(),
  mesure_proposee: schemaMesureProposee.optional(),
});
export type SaisieConstat = z.infer<typeof schemaConstat>;

/** Formulaire de constat emporté sur l'appareil : la version citée par l'ordre (D-107). */
export const schemaFormulaireTerrain = z.object({ id: uuid, code: z.string(), nom: z.string(), version: z.number().int(), points: z.array(schemaPointFormulaire) });
export type FormulaireTerrain = z.infer<typeof schemaFormulaireTerrain>;

/**
 * Ordre de mission emporté sur l'appareil (GET /terrain/travail, page « ordres_mission ») : ordre ouvert
 * dont l'agent est le contrôleur, cible, période, consignes, formulaire complet de la version citée,
 * version (D-130 : elle n'avance qu'à la réaffectation, l'annulation ou la clôture).
 */
export const schemaOrdreMissionTerrain = z.object({
  id: uuid,
  numero: z.string(),
  objet_type: z.enum(CODES_OBJETS_ORDRE),
  objet_id: uuid.nullable(),
  objet_libelle: z.string(),
  lieu: z.string().nullable(),
  position: schemaPosition.nullable(),
  periode_debut: schemaJourCivil,
  periode_fin: schemaJourCivil,
  consignes: z.string().nullable(),
  formulaire: schemaFormulaireTerrain,
  version: z.number().int(),
  etat: z.enum(CODES_ETATS_ORDRE),
});
export type OrdreMissionTerrain = z.infer<typeof schemaOrdreMissionTerrain>;

/** Opération « constat » de la synchronisation terrain : vise un ordre de mission et la version connue de l'appareil. */
export const schemaOperationConstat = schemaEnveloppeOperation('constat', 'ordre_mission', schemaConstat);
export type OperationConstat = z.infer<typeof schemaOperationConstat>;
export const schemaLotOperationsConstat = z.strictObject({ operations: z.array(schemaOperationConstat).min(1).max(200) });

export const schemaTranchageConflit = z.strictObject({ decision: z.enum(DECISIONS_CONFLIT), motif: z.string().trim().min(3).max(500).optional() });

export const schemaFiltreConstats = z.strictObject({
  ordre_id: uuid.optional(),
  etat: z.enum(CODES_ETATS_CONSTAT).optional(),
  conclusion: z.enum(Object.keys(CONCLUSIONS_CONSTAT) as [ConclusionConstat, ...ConclusionConstat[]]).optional(),
  limite: z.coerce.number().int().min(1).max(500).default(100),
});

/**
 * Contrôle d'une grille de réponses contre les points du formulaire : chaque point répondu une fois,
 * aucun code inconnu, observation obligatoire quand le point l'exige ou quand il est non conforme,
 * photo présente quand le point l'exige et qu'il n'est pas sans objet. Liste vide : grille recevable.
 */
export function verifierGrilleConstat(points: readonly PointFormulaire[], reponses: readonly ReponseConstat[]): { champ: string; message: string }[] {
  const problemes: { champ: string; message: string }[] = [];
  const parCode = new Map(points.map((p) => [p.code, p]));
  const vus = new Set<string>();
  reponses.forEach((r, i) => {
    const p = parCode.get(r.code);
    if (!p) {
      problemes.push({ champ: `reponses.${i}.code`, message: `Point inconnu du formulaire : ${r.code}` });
      return;
    }
    if (vus.has(r.code)) problemes.push({ champ: `reponses.${i}.code`, message: `Point répondu deux fois : ${r.code}` });
    vus.add(r.code);
    const observation = r.observation?.trim() ?? '';
    if ((p.observation === 'obligatoire' || r.reponse === 'non_conforme') && observation.length === 0) {
      problemes.push({ champ: `reponses.${i}.observation`, message: `Observation attendue pour « ${p.libelle} »` });
    }
    if (p.photo_exigee && r.reponse !== 'sans_objet' && !r.photo_document_id && !r.photo_id_client) {
      problemes.push({ champ: `reponses.${i}.photo_document_id`, message: `Photo exigée pour « ${p.libelle} »` });
    }
  });
  for (const p of points) if (!vus.has(p.code)) problemes.push({ champ: 'reponses', message: `Point sans réponse : ${p.libelle}` });
  return problemes;
}

/** Conclusion d'un constat : non conforme dès qu'un point l'est ; sans objet si tous le sont ; conforme sinon. */
export function conclusionConstat(reponses: readonly Pick<ReponseConstat, 'reponse'>[]): ConclusionConstat {
  if (reponses.some((r) => r.reponse === 'non_conforme')) return 'non_conforme';
  if (reponses.length > 0 && reponses.every((r) => r.reponse === 'sans_objet')) return 'sans_objet';
  return 'conforme';
}

// Mesures correctives et transmission -----------------------------------------------------------

export const schemaCreationMesureCorrective = z.strictObject({
  description: z.string().trim().min(3).max(1000),
  /** Délai accordé, en jours ouvrés du calendrier de la collectivité (D-045). */
  delai_jours: z.number().int().min(1).max(365),
  point_code: z.string().trim().max(41).optional(),
});

export const schemaClotureMesure = z.strictObject({
  etat: z.enum(['realisee', 'non_realisee']),
  observation: z.string().trim().max(1000).optional(),
});

export const schemaFiltreMesures = z.strictObject({
  etat: z.enum(CODES_ETATS_MESURE).optional(),
  echues: z.enum(['oui', 'non']).optional(),
  constat_id: uuid.optional(),
  limite: z.coerce.number().int().min(1).max(500).default(100),
});

export const schemaTransmissionConstat = z.strictObject({
  /** Autorité destinataire ; par défaut, le maire ou le président (rôle maire_president). */
  autorite_appartenance_id: uuid.optional(),
  message: z.string().trim().max(2000).optional(),
});

export const schemaFiltreCarteControles = z.strictObject({
  bbox: schemaBbox.optional(),
  etat: z.enum(CODES_ETATS_ORDRE).optional(),
});

// Carte --------------------------------------------------------------------------------------

export interface ProprietesCarteOrdreMission {
  objet: 'ordre_mission';
  id: string;
  numero: string;
  objet_type: ObjetOrdreMission;
  objet_libelle: string;
  etat: EtatOrdreMission;
  periode_debut: string;
  periode_fin: string;
  /** Conclusion du constat retenu, s'il existe. */
  conclusion: ConclusionConstat | null;
}
